import { createContext } from "react";
import { create, type StoreApi, type UseBoundStore } from "zustand";
import type { Calc } from "../types";

export interface CalculatorState {
  api: Calc.Api.Endpoints;

  links: Calc.Links.Links;

  config: Calc.Calculator.Calculator;

  isCustomActive: boolean;
  isCustomScrollIntoViewAvailable: boolean;
  setCustomActive: (isCustomActive: boolean) => void;

  activePack: string;
  packs: Calc.Pack.Pack[];
  setActivePack: (activePack: string) => void;

  isTrialActive: boolean;
  setTrialActive: (isTrialActive: boolean) => void;

  isRequestDemoActive: boolean;

  isPromoCodeDialogOpen: boolean;
  setPromoCodeDialogOpen: (isPromoCodeDialogOpen: boolean) => void;

  promoCode?: string;
  setPromoCode: (promoCode?: string) => void;

  isPaymentMethodDialogOpen: boolean;
  setPaymentMethodDialogOpen: (isPaymentMethodDialogOpen: boolean) => void;

  activePeriod: string;
  setActivePeriod: (activePeriod: string) => void;

  modules: Map<
    string,
    {
      active: boolean;
      isDisableConfirmRequired: boolean;
      values: Map<string, number>;
    }
  >;
  setModuleActive: (id: string, active: boolean) => void;
  setModuleValue: (id: string, optionId?: string, optionValue?: number) => void;
  setModuleConfirmed: (id: string) => void;
  getActiveModulesCount: () => number;

  getRequestData: () => Calc.Api.CalculatorUpdateRequest;

  getState: () => CalculatorState;
}

export interface CalculatorContextProps {
  useCalculator: UseBoundStore<StoreApi<CalculatorState>>;
}

export const CalculatorContext = createContext<CalculatorContextProps | null>(
  null,
);

export const createCalculatorContext = ({
  api,
  links,
  calculator,
}: {
  api: Calc.Api.Endpoints;
  links: Calc.Links.Links;
  calculator: Calc.Calculator.Calculator;
}) => {
  const store = create<CalculatorState>((set, get) => ({
    api,

    links,

    config: calculator,

    isCustomActive: calculator.isCustomActive,
    isCustomScrollIntoViewAvailable: calculator.isCustomActive === false,
    setCustomActive: (isCustomActive) => {
      set({
        isCustomActive,
        isCustomScrollIntoViewAvailable: true,
      });
    },

    activePack: calculator.activePack,
    packs: calculator.packs,
    setActivePack: (activePack) => {
      set({ activePack });
    },

    isTrialActive: calculator.isTrialActive,
    setTrialActive: (isTrialActive) => {
      set({ isTrialActive });
    },

    isRequestDemoActive: calculator.isRequestDemoActive,

    isPromoCodeDialogOpen: false,
    setPromoCodeDialogOpen: (isPromoCodeDialogOpen) => {
      set({ isPromoCodeDialogOpen });
    },

    promoCode: calculator.promoCode,
    setPromoCode: (promoCode?: string) => {
      set({ promoCode });
    },

    isPaymentMethodDialogOpen: false,
    setPaymentMethodDialogOpen: (isPaymentMethodDialogOpen) => {
      set({ isPaymentMethodDialogOpen });
    },

    activePeriod: calculator.activePeriod,
    setActivePeriod: (activePeriod) => {
      set({ activePeriod });
    },

    modules: new Map(
      calculator.modules.map((module) => [
        module.id,
        {
          active: module.active,
          isDisableConfirmRequired: module.isSubscribed,
          values: (() => {
            if (module.type === "counter") {
              return new Map<string, number>([
                [module.counter.id, module.counter.value],
              ]);
            }

            if (module.type === "select") {
              return new Map<string, number>(
                module.select.map((select) => [select.id, select.value]),
              );
            }

            return new Map<string, number>();
          })(),
        },
      ]),
    ),
    setModuleActive: (id, active) => {
      const modules = new Map(get().modules);

      if (active === false) {
        // Prevent to disable all modules
        const count = getActiveModulesCount(modules);

        if (count <= 1) {
          return;
        }
      }

      const module = modules.get(id);

      if (module !== undefined) {
        modules.set(id, {
          ...module,
          active,
        });
      }

      set({
        modules,
      });
    },
    setModuleValue: (id, optionId, optionValue) => {
      const modules = new Map(get().modules);
      const module = modules.get(id);

      if (module !== undefined) {
        const values = new Map(module.values);

        if (optionId !== undefined && optionValue !== undefined) {
          values.set(optionId, optionValue);
        }

        modules.set(id, {
          ...module,
          values,
        });
      }

      set({
        modules,
      });
    },
    setModuleConfirmed: (id) => {
      const modules = new Map(get().modules);

      const module = modules.get(id);

      if (module !== undefined) {
        modules.set(id, {
          ...module,
          isDisableConfirmRequired: false,
        });
      }

      set({
        modules,
      });
    },
    getActiveModulesCount: () => {
      const activeModules = Array.from(get().modules).filter(
        (entry) => entry[1].active,
      );

      return activeModules.length;
    },

    getRequestData: () => {
      const state = get();

      return {
        isCustomActive: state.isCustomActive,
        isTrialActive: state.isTrialActive,
        promoCode: state.promoCode,
        activePack: state.activePack,
        activePeriod: state.activePeriod,
        modules: Object.fromEntries(
          Array.from(state.modules.entries()).map(([id, module]) => [
            id,
            {
              active: module.active,
              values: Object.fromEntries(module.values.entries()),
            },
          ]),
        ),
      };
    },

    getState: () => {
      return get();
    },
  }));

  return {
    useCalculator: store,
  };
};

const getActiveModulesCount = (
  modules: Map<
    string,
    {
      active: boolean;
    }
  >,
) => {
  const activeModules = Array.from(modules).filter((entry) => entry[1].active);

  return activeModules.length;
};
